import React, { useEffect, useState, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileUpload } from "@fortawesome/free-solid-svg-icons";
import axiosInstance from "../utils/axios-instance";
import UseAuth from "../utils/use-auth";
import UseAngelChat from "../utils/use-angel-chat";
import { toast } from "sonner";
import { ExtractDocumentText } from "../utils/extract-document-text";
import { FaPaperPlane, FaSpinner } from 'react-icons/fa';
import { AiOutlinePaperClip } from 'react-icons/ai';
import { RiRobot2Fill, RiUserFill } from "react-icons/ri";
import { marked } from "marked";

const PdfChatArea = () => {
  const { currentConversation, handleSelectConversation } = UseAngelChat();
  const { user } = UseAuth();
  const [messages, setMessages] = useState({ totalCount: 0, data: [] });
  const [newMessage, setNewMessage] = useState("");
  const [role, setRole] = useState("");
  const [file, setFile] = useState(null);
  const [loadingState, setLoadingState] = useState(null);
  const messagesEndRef = useRef(null);
  const [streamedText, setStreamedText] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const streamInterval = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, streamedText]);

  useEffect(() => {
    const findMessages = async () => {
      try {
        const res = await axiosInstance(`/chat/find/messages/${currentConversation?.id}`);
        setMessages(res.data);
      } catch (error) {}
    };
    currentConversation?.id ? findMessages() : setMessages({ totalCount: 0, data: [] });
  }, [currentConversation?.id]);

  useEffect(() => {
    return () => { if (streamInterval.current) clearInterval(streamInterval.current); };
  }, []);

  const getProcessedStreamText = () => {
    try {
      let processedText = streamedText;
      const codeBlockCount = (streamedText.match(/```/g) || []).length;
      if (codeBlockCount % 2 !== 0) processedText += "\n```";
      const asteriskCount = (streamedText.match(/\*/g) || []).length;
      if (asteriskCount % 2 !== 0) processedText += "*";
      return marked(processedText);
    } catch { return streamedText; }
  };

  const simulateStreaming = (text) => {
    setStreamedText("");
    setIsStreaming(true);
    const tokens = text.split(/(?<=\s+)/);
    let i = 0;
    if (streamInterval.current) clearInterval(streamInterval.current);
    streamInterval.current = setInterval(() => {
      if (i < tokens.length) { setStreamedText(prev => prev + tokens[i]); i++; }
      else { clearInterval(streamInterval.current); setIsStreaming(false); }
    }, 35);
  };

  const handleSendMessage = async (msg) => {
    const text = newMessage || msg;
    if (!text?.trim()) return;
    setLoadingState("Preparing");
    const message = { conversationId: currentConversation?.id, message: text, userId: user?.id, newRole: role };
    setMessages(prev => ({ totalCount: prev.totalCount + 1, data: [...prev.data, message] }));
    setNewMessage("");
    try {
      const res = await axiosInstance.post(`/resume-chat/send/message`, { ...message, messages: messages?.data });
      simulateStreaming(res.data.message.message);
      setMessages(prev => ({ totalCount: prev.totalCount + 1, data: [...prev.data, res.data.message] }));
      if (!currentConversation?.id) handleSelectConversation(res.data.conversation);
      setLoadingState(null);
    } catch { setLoadingState(null); }
  };

  const handleFile = (e) => {
    const f = e.target.files[0];
    if (f && (f.type === "application/pdf" || f.name.endsWith(".docx"))) setFile(f);
    else toast.error("Please upload a PDF or DOCX file");
  };

  const handleFileFromInput = async (e) => {
    const f = e.target.files[0];
    e.target.value = "";
    if (f && (f.type === "application/pdf" || f.name.endsWith(".docx"))) {
      setLoadingState("fetching-text");
      const text = await ExtractDocumentText(f);
      handleSendMessage(text);
    } else toast.error("Please upload a PDF or DOCX file");
  };

  const HandleStartConversation = async () => {
    setLoadingState("fetching-text");
    const text = await ExtractDocumentText(file);
    handleSendMessage(text);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMessage(); }
  };

  return (
    <div className="flex flex-col h-full bg-[#0f1829]">
      {/* Header */}
      {currentConversation && (
        <div className="border-b border-white/10 bg-[#111d42] px-5 py-4 flex-shrink-0">
          <h2 className="ps-8 lg:ps-0 text-xl font-bold text-white">Angel — CV Editor</h2>
          <p className="ps-8 lg:ps-0 text-sm text-white/40">Tailoring your CV to match job descriptions</p>
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 overflow-hidden">
        {!currentConversation && !loadingState ? (
          /* ─── Welcome / Upload Screen ─── */
          <div className="flex flex-col items-center justify-center h-full p-6 text-center">
            <div className="max-w-md w-full mx-auto">
              {/* Avatar */}
              <div className="w-14 h-14 rounded-2xl bg-[#1B2B5E] border border-white/10 flex items-center justify-center mx-auto mb-5">
                <RiRobot2Fill className="text-[#F05A22] text-2xl" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Angel — CV Editor</h2>
              <p className="text-white/40 text-sm mb-8 leading-relaxed">
                Upload your CV and paste the job description to get tailored improvements.
              </p>

              <div className="space-y-4 text-left">
                {/* Upload zone */}
                <label
                  htmlFor="upload-resume"
                  className="flex flex-col items-center gap-2 p-6 border-2 border-dashed border-white/15 rounded-xl cursor-pointer hover:border-[#F05A22]/50 hover:bg-[#F05A22]/5 transition-all"
                >
                  <FontAwesomeIcon icon={faFileUpload} className="text-[#F05A22] text-2xl" />
                  <span className="text-sm font-medium text-white/70">Upload your CV</span>
                  <span className="text-xs text-white/30">PDF or DOCX</span>
                  {file && (
                    <span className="text-xs text-[#F05A22] mt-1 truncate max-w-full">{file.name}</span>
                  )}
                  <input id="upload-resume" className="hidden" type="file" accept=".pdf,.docx" onChange={handleFile} />
                </label>

                {/* Job description */}
                <textarea
                  className="w-full p-3.5 border border-white/10 bg-[#162035] text-white placeholder-white/25 rounded-xl resize-none focus:outline-none focus:border-[#F05A22]/40 focus:ring-1 focus:ring-[#F05A22]/20 text-sm transition-all"
                  rows={3}
                  placeholder="Paste the job description here..."
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                />

                <button
                  className="w-full py-3 rounded-xl text-white font-semibold text-sm bg-[#1B2B5E] border border-white/15 hover:bg-[#243875] hover:border-[#F05A22]/30 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                  disabled={!file || !role}
                  onClick={HandleStartConversation}
                >
                  Edit My CV
                </button>
              </div>
            </div>
          </div>
        ) : (
          /* ─── Chat View ─── */
          <div className="h-full flex flex-col">
            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages?.data?.map((message, index) => {
                if (index === 0) {
                  return (
                    <div key={index} className="w-full md:w-[85%] lg:w-[75%] mx-auto">
                      <div className="bg-[#1B2B5E]/40 border border-white/10 p-4 rounded-2xl text-sm text-white/60">
                        <p className="font-semibold text-white/80 mb-1">Updating CV for role:</p>
                        <p>{message?.newRole}</p>
                      </div>
                    </div>
                  );
                }

                const isLastAssistant = !message.userId && index === messages.data.length - 1;

                return (
                  <div
                    key={index}
                    className={`flex items-start gap-3 ${message.userId ? "justify-end" : ""} w-full md:w-[85%] lg:w-[75%] mx-auto`}
                  >
                    {!message.userId && (
                      <div className="w-8 h-8 rounded-lg bg-[#1B2B5E] border border-white/10 flex items-center justify-center flex-shrink-0">
                        <RiRobot2Fill className="text-[#F05A22] text-sm" />
                      </div>
                    )}
                    <div
                      className={`max-w-[80%] p-4 rounded-2xl text-sm leading-relaxed ${
                        message.userId
                          ? "bg-[#1B2B5E] text-white rounded-tr-sm border border-white/10"
                          : "bg-[#162035] border border-white/10 text-white/80 rounded-tl-sm"
                      }`}
                    >
                      {isLastAssistant && isStreaming ? (
                        <div className="leading-7 msg-main-box" dangerouslySetInnerHTML={{ __html: getProcessedStreamText() }} />
                      ) : (
                        <div className="leading-7 msg-main-box" dangerouslySetInnerHTML={{ __html: marked(message?.message) }} />
                      )}
                      {isLastAssistant && isStreaming && (
                        <span className="inline-block w-1.5 h-4 bg-[#F05A22] ml-1 animate-pulse rounded-full" />
                      )}
                    </div>
                    {message.userId && (
                      <div className="w-8 h-8 rounded-lg bg-[#1e2d4a] border border-white/10 flex items-center justify-center flex-shrink-0">
                        <RiUserFill className="text-white/50 text-sm" />
                      </div>
                    )}
                  </div>
                );
              })}

              {loadingState && (
                <div className="flex items-start gap-3 w-full md:w-[85%] lg:w-[75%] mx-auto">
                  <div className="w-8 h-8 rounded-lg bg-[#1B2B5E] border border-white/10 flex items-center justify-center flex-shrink-0">
                    <RiRobot2Fill className="text-[#F05A22] text-sm" />
                  </div>
                  <div className="bg-[#162035] border border-white/10 p-4 rounded-2xl rounded-tl-sm">
                    <div className="flex items-center gap-2 text-sm text-white/50">
                      <FaSpinner className="animate-spin text-[#F05A22]" />
                      <span className="capitalize">{loadingState.replace(/-/g, " ")}...</span>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="border-t border-white/10 bg-[#111d42] p-4 flex-shrink-0">
              <div className="relative">
                <textarea
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Message Angel..."
                  className="w-full p-3.5 pr-24 border border-white/10 bg-[#162035] text-white placeholder-white/25 rounded-xl resize-none focus:outline-none focus:border-[#F05A22]/40 focus:ring-1 focus:ring-[#F05A22]/20 transition-all text-sm"
                  rows={1}
                />
                <div className="absolute right-3 bottom-2.5 flex items-center gap-1">
                  <label htmlFor="file-upload-angel" className="text-white/30 hover:text-[#F05A22] cursor-pointer transition-colors p-1">
                    <AiOutlinePaperClip className="text-2xl" />
                    <input id="file-upload-angel" type="file" className="hidden" onChange={handleFileFromInput} accept=".pdf,.docx" />
                  </label>
                  <button
                    onClick={() => handleSendMessage()}
                    disabled={!newMessage || loadingState || isStreaming}
                    className={`p-2.5 rounded-lg transition-all ${newMessage && !isStreaming
                      ? "bg-[#F05A22] text-white hover:bg-[#c8471a]"
                      : "bg-white/5 text-white/20 cursor-not-allowed"}`}
                  >
                    {loadingState || isStreaming ? <FaSpinner className="animate-spin text-sm" /> : <FaPaperPlane className="text-sm" />}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PdfChatArea;
